import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { FaCheck, FaEdit, FaEye, FaMoneyBillWave } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { BsThreeDotsVertical } from "react-icons/bs";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PageHeading from "../../PageHeading";
import ConfirmActionModal from "../../ConfirmDeleteModal";
import ExportOptions from "../../ExportOptions";
import axios from "axios";
import TableSkeleton from "../../TableSkeleton";
import { useAuth } from "../../../utils/AuthContext";
import EmergencyBillModal from "./EmergencyBillModal";
import EmergencyViewBillModal from "./EmergencyViewBillModal";
import SmartDropdown from "../../Shared/SmartDropdown";
import EmergencyAddPaymentModal from "./EmergencyAddPaymentModal";

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const EmergencyBill = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  
  // Modals
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [generateBillModalOpen, setGenerateBillModalOpen] = useState(false);
  const [viewBillModalOpen, setViewBillModalOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);

  // Data States
  const [viewBillData, setViewBillData] = useState(null);
  const [editBillId, setEditBillId] = useState(null);
  const [editBillData, setEditBillData] = useState(null);
  const [paymentBillData, setPaymentBillData] = useState(null);
  const [bills, setBills] = useState([]);
  
  // Search & Pagination
  const [searchQuery, setSearchQuery] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Dropdown Refs
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownTriggerRefs = useRef({});

  const itemsPerPage = 10;
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const actionButtonClass = "size-7 rounded-full text-sm flex items-center justify-center transition-all duration-300";
  const { user } = useAuth();

  const fetchBills = useCallback(async () => {
    setIsLoading(true);
    try {
      const url = `https://api.muktihospital.com/api/emergency/emergency-bills`;
      const response = await axios.get(url, {
        headers: {
          "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
        },
      });
      const { bills, total } = response.data;
      setBills(bills || []);
      setTotalPages(Math.ceil(total / itemsPerPage) || 1);
      setTotalRecords(total || 0);
    } catch (err) {
      console.error("Error fetching bills:", err);
      toast.error(err.response?.data?.error || "Failed to load bills.", { autoClose: 2000 });
      setBills([]);
      setTotalPages(1);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  }, [itemsPerPage]);

  const handleRefreshBills = useCallback(async () => {
    await fetchBills();
  }, [fetchBills]);

  const handleDelete = useCallback(async (billIds) => {
    try {
      await Promise.all(
        billIds.map((id) =>
          axios.delete(`https://api.muktihospital.com/api/emergency/emergency-bills/delete/${id}`, {
            headers: { "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079" },
          })
        )
      );
      await handleRefreshBills();
      setSelectedRows([]);
      toast.success(`${billIds.length} bill(s) deleted successfully!`, { autoClose: 2000 });
    } catch (err) {
      console.error("Error deleting bills:", err);
      toast.error("Failed to delete bill(s).", { autoClose: 2000 });
    }
  }, [handleRefreshBills]);

  const handleEdit = useCallback(async (bill) => {
      setEditBillId(bill.id);
      setEditBillData(bill);
      setGenerateBillModalOpen(true);
  }, []);

  const handleView = useCallback((bill) => {
    setViewBillData(bill);
    setViewBillModalOpen(true);
  }, []);

  const handleAddMoney = (bill) => {
    setPaymentBillData(bill);
    setPaymentModalOpen(true);
  };

  useEffect(() => {
    fetchBills();
  }, [fetchBills]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery]);

  const handlePageChange = useCallback((page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
      setSelectedRows([]);
    }
  }, [totalPages]);

  const handleSelectAll = useCallback((e) => {
    const validIds = bills.filter((bill) => bill.id != null).map((bill) => bill.id);
    setSelectedRows(e.target.checked ? validIds : []);
  }, [bills]);

  const handleRowSelect = useCallback((billId) => {
    if (billId == null) return;
    setSelectedRows((prev) =>
      prev.includes(billId) ? prev.filter((id) => id !== billId) : [...prev, billId]
    );
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, []);

  const toggleDropdown = () => setDropdownOpen((prev) => !prev);

  const headers = [
    "SL No",
    "BILL NO",
    "Patient Name",
    "DATE",
    "DISCOUNT (৳)",
    "NET AMOUNT (৳)",
    "PAID AMOUNT (৳)",
    "SUBMITTED BY",
    "AUTHORIZED BY",
  ];

  const skeletonColumns = [
    { width: 20, height: 20, isCircle: true, className: "print-hidden-in-print" },
    { width: 50, height: 16 },
    { width: 100, height: 16 },
    { width: 100, height: 16 },
    { width: 100, height: 16 },
    { width: 100, height: 16 },
    { width: 100, height: 16 },
    { width: 150, height: 16 },
    { width: 150, height: 16 },
    { width: 150, height: 16 },
    { width: 120, height: 16, className: "print-hidden-in-print" },
  ];

  const dataMapper = (bill) => [
    (currentPage - 1) * itemsPerPage + bills.indexOf(bill) + 1,
    bill.billNumber || "-",
    bill.patientName || "",
    bill.createdAt ? new Date(bill.createdAt).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "-",
    bill.discount?.toFixed(2) || "0.00",
    bill.netAmount?.toFixed(2) || "0.00",
    bill.paidAmount?.toFixed(2) || "0.00",
    user?.name || "Unknown",
    bill.authorizedBy || "N/A"
  ].join("\t");

  const breadcrumbs = useMemo(() => [
      { label: "Dashboard", url: "/" },
      { label: "Emergency" },
      { label: "Emergency Bills" },
    ], []
  );

  const allVisibleSelected = useMemo(
    () => bills.length > 0 && bills.every((bill) => bill.id && selectedRows.includes(bill.id)),
    [bills, selectedRows]
  );

  return (
    <div>
      <ToastContainer position="top-right" className="print-hidden" />
      <div className="print-hidden">
        <PageHeading title="Emergency Bills" breadcrumbs={breadcrumbs} />
      </div>
      <div className="bg-white shadow-sm rounded-lg">
        {/* Header Controls */}
        <div className="flex justify-between flex-wrap border-b border-dashed border-M-text-color/50 p-4 gap-4 print-hidden">
          <div className="inline-flex items-center gap-3 shrink-0">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
              Bill List
            </h2>
            {selectedRows.length > 0 && (
              <button
                onClick={() => setDeleteModalOpen(true)}
                className={`${actionButtonClass} bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white`}
                title="Delete selected bills"
              >
                <RiDeleteBin6Line />
              </button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end gap-3 w-full sm:w-auto">
            <input
              type="text"
              className="max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color shrink-0"
              placeholder="Search by bill no..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
            <div className="flex items-center justify-between gap-3 w-full sm:w-auto">
              <button
                onClick={() => {
                  setEditBillId(null);
                  setEditBillData(null);
                  setGenerateBillModalOpen(true);
                }}
                className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0 px-4 py-2 text-sm"
              >
                + Emergency Bill
              </button>
              <ExportOptions
                data={bills}
                dropdownOpen={dropdownOpen}
                toggleDropdown={toggleDropdown}
                title="Emergency Bill List"
                headers={headers}
                dataMapper={dataMapper}
              />
            </div>
          </div>
        </div>

        <style>{`@media print { .print-hidden, .print-hidden-in-print { display: none !important; } .print-table { width: 100%; border-collapse: collapse; } .print-table th, .print-table td { border: 1px solid #000; padding: 8px; font-size: 12px; } }`}</style>

        {/* Table */}
        <div className="overflow-x-auto">
          <div className="print-wrapper">
            <table className="min-w-[1440px] w-full divide-y divide-gray-200 print-table">
              <thead>
                <tr className="bg-gray-100">
                  <th className="text-left print-hidden-in-print p-3">
                    <div className="relative left-3 -top-[2px]">
                      <input
                        type="checkbox"
                        id="selectAll"
                        className="hidden peer print-hidden"
                        onChange={handleSelectAll}
                        checked={allVisibleSelected}
                        disabled={bills.length === 0}
                      />
                      <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                        <FaCheck className="text-slate-100" />
                      </span>
                      <label htmlFor="selectAll" className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"></label>
                    </div>
                  </th>
                  {headers.map((header, idx) => (
                    <th key={idx} className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                      {header}
                    </th>
                  ))}
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print">
                    ACTIONS
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <TableSkeleton rowCount={itemsPerPage} columns={skeletonColumns} />
                ) : bills.length === 0 ? (
                  <tr className="print-hidden">
                    <td colSpan={11} className="text-center py-5 text-gray-500">
                      {searchQuery ? "No bills found matching your search." : "No bills available."}
                    </td>
                  </tr>
                ) : (
                  <>
                    {bills.map((bill, index) => (
                      <tr key={`normal-${bill.id}`} className="border-b hover:bg-gray-50 print-hidden">
                        <td className="text-left print-hidden-in-print p-3">
                          <div className="relative left-3 -top-[2px]">
                            <input
                              type="checkbox"
                              id={`bill-${index}`}
                              className="hidden peer"
                              checked={selectedRows.includes(bill.id)}
                              onChange={() => handleRowSelect(bill.id)}
                            />
                            <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0">
                              <FaCheck className="text-slate-100" />
                            </span>
                            <label htmlFor={`bill-${index}`} className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6"></label>
                          </div>
                        </td>
                        <td className="p-3 text-sm">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                        <td className="p-3 text-sm">{bill.billNumber || "-"}</td>
                        <td className="p-3 text-sm">{bill.patientName || ""}</td>
                        <td className="p-3 text-sm">
                          {bill.createdAt ? new Date(bill.createdAt).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "-"}
                        </td>
                        <td className="p-3 text-sm">{bill.discount?.toFixed(2) || "0.00"}</td>
                        <td className="p-3 text-sm">{bill.netAmount?.toFixed(2) || "0.00"}</td>
                        <td className="p-3 text-sm">{bill.paidAmount?.toFixed(2) || "0.00"}</td>
                        <td className="p-3 text-sm">{user?.name || "Unknown"}</td>
                        <td className="p-3 text-sm">{bill.authorizedBy || "N/A"}</td>
                        <td className="p-3 text-right print-hidden-in-print">
                           {/* ✅ SMART DROPDOWN LOGIC */}
                           <div className="relative">
                              <button
                                ref={(el) => (dropdownTriggerRefs.current[bill.id] = el)}
                                onClick={() => setActiveDropdown(activeDropdown === bill.id ? null : bill.id)}
                                className="size-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors text-gray-500"
                              >
                                <BsThreeDotsVertical size={14} />
                              </button>

                              <SmartDropdown
                                isOpen={activeDropdown === bill.id}
                                triggerRef={{ current: dropdownTriggerRefs.current[bill.id] }}
                                onClose={() => setActiveDropdown(null)}
                                options={{ width: 160, align: "right" }}
                              >
                                <div className="py-1">
                                  {/* Add Payment Button */}
                                  <button
                                    onClick={() => {
                                      handleAddMoney(bill);
                                      setActiveDropdown(null);
                                    }}
                                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                                  >
                                    <FaMoneyBillWave className="text-green-600" size={14} />
                                    <span>Add Money</span>
                                  </button>

                                  <button
                                    onClick={() => { handleView(bill); setActiveDropdown(null); }}
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                  >
                                    <FaEye className="text-blue-500" /> View Details
                                  </button>

                                  <button
                                    onClick={() => { handleEdit(bill); setActiveDropdown(null); }}
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                  >
                                    <FaEdit className="text-green-600" /> Edit Bill
                                  </button>

                                  <button
                                    onClick={() => { setSelectedRows([bill.id]); setDeleteModalOpen(true); setActiveDropdown(null); }}
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 flex items-center gap-2"
                                  >
                                    <RiDeleteBin6Line className="text-red-500" /> Delete
                                  </button>
                                </div>
                              </SmartDropdown>
                           </div>
                        </td>
                      </tr>
                    ))}
                    {/* Print Rows Logic (Hidden in UI, Visible in Print) */}
                    {bills.length > 0 && bills.map((bill, index) => (
                        <tr key={`print-${bill.id}`} className="border-b print-all-rows" style={{ display: "none" }}>
                            <td className="p-3 text-sm print-hidden-in-print"></td>
                            <td className="p-3 text-sm">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                            <td className="p-3 text-sm">{bill.billNumber || "-"}</td>
                            <td className="p-3 text-sm">{bill.patientName || ""}</td>
                            <td className="p-3 text-sm">{bill.createdAt ? new Date(bill.createdAt).toLocaleDateString("en-US") : "-"}</td>
                            <td className="p-3 text-sm">{bill.discount?.toFixed(2) || "0.00"}</td>
                            <td className="p-3 text-sm">{bill.netAmount?.toFixed(2) || "0.00"}</td>
                            <td className="p-3 text-sm">{bill.paidAmount?.toFixed(2) || "0.00"}</td>
                            <td className="p-3 text-sm">{user?.name || "Unknown"}</td>
                            <td className="p-3 text-sm">{bill.authorizedBy || "N/A"}</td>
                            <td className="p-3 text-sm print-hidden-in-print"></td>
                        </tr>
                    ))}
                  </>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="p-5 text-end print-hidden">
              <div className="inline-flex items-center justify-between w-full">
                <p className="text-sm text-gray-600">
                  Records: {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, totalRecords)} of {totalRecords}
                </p>
                <nav aria-label="Pagination">
                  <ul className="inline-flex items-center justify-end border-collapse border border-M-text-color/20 rounded-md overflow-hidden">
                    <li>
                      <button
                        className={`table-pagination-item ${currentPage === 1 ? "disabled" : ""}`}
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        <MdKeyboardArrowLeft />
                      </button>
                    </li>
                    {[...Array(totalPages)].map((_, i) => (
                        <li key={i}>
                          <button
                            className={`table-pagination-item ${currentPage === i + 1 ? "bg-M-primary-color text-white" : ""}`}
                            onClick={() => handlePageChange(i + 1)}
                          >
                            {i + 1}
                          </button>
                        </li>
                    ))}
                    <li>
                      <button
                        className={`table-pagination-item ${currentPage === totalPages ? "disabled" : ""}`}
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        <MdKeyboardArrowRight />
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmActionModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedRows([]);
        }}
        onConfirm={() => handleDelete(selectedRows)}
        ids={selectedRows}
        entityName="bill"
        title="Confirm Delete"
        message={selectedRows.length > 1 ? `Do you really want to delete ${selectedRows.length} bill(s)?` : `Do you really want to delete this bill?`}
        confirmText="Delete"
        cancelText="Cancel"
      />

      <EmergencyBillModal
        isOpen={generateBillModalOpen}
        onClose={() => {
          setGenerateBillModalOpen(false);
          setEditBillId(null);
          setEditBillData(null);
        }}
        onSave={async (newBill, isPrintAction = false) => {
          await handleRefreshBills();
        }}
        billId={editBillId}
        initialData={editBillData}
      />

      <EmergencyViewBillModal
        isOpen={viewBillModalOpen}
        onClose={() => {
          setViewBillModalOpen(false);
          setViewBillData(null);
        }}
        bill={viewBillData}
        onEdit={(bill) => {
            setViewBillModalOpen(false);
            handleEdit(bill);
        }}
        onDelete={(ids) => {
            setViewBillModalOpen(false);
            setSelectedRows(ids);
            setDeleteModalOpen(true);
        }}
      />

      {/* ✅ Add Payment Modal */}
      <EmergencyAddPaymentModal
        isOpen={paymentModalOpen}
        onClose={() => {
          setPaymentModalOpen(false);
          setPaymentBillData(null);
        }}
        bill={paymentBillData}
        onPaymentSuccess={handleRefreshBills}
      />
    </div>
  );
};

export default EmergencyBill;